import { products } from "@/mocks/products";
import { categories } from "@/mocks/categories";
import { lookMoments } from "@/mocks/looks";
import { inspirations } from "@/mocks/inspirations";
import { mainNav } from "@/mocks/navigation";
import { deliveryMethods } from "@/config/storeConfig";
import type { ImageAsset, ImageStandard } from "@/pages/painel/types";

/* ------------------------------------------------------------------ */
/* Padrões aprovados de imagem                                          */
/* ------------------------------------------------------------------ */

export const imageStandards: ImageStandard[] = [
  { key: "hero-home", label: "Hero principal da home", quantity: 1, width: 1600, height: 1000, ratio: "8:5" },
  { key: "category-card", label: "Card de categoria", quantity: 8, width: 700, height: 900, ratio: "7:9" },
  { key: "category-hero-desktop", label: "Hero de categoria desktop", quantity: 8, width: 1920, height: 760, ratio: "48:19" },
  { key: "category-hero-mobile", label: "Hero de categoria mobile", quantity: 8, width: 900, height: 1200, ratio: "3:4" },
  { key: "product", label: "Produtos e cores", quantity: 70, width: 700, height: 900, ratio: "7:9" },
  { key: "look", label: "Looks", quantity: 8, width: 700, height: 900, ratio: "7:9" },
  { key: "inspiration-square", label: "Inspirações quadradas", quantity: 5, width: 700, height: 700, ratio: "1:1" },
  { key: "inspiration-tall", label: "Inspirações verticais", quantity: 3, width: 700, height: 950, ratio: "14:19" },
  { key: "brand-story", label: "História da marca", quantity: 1, width: 900, height: 1100, ratio: "9:11" },
  { key: "editorial", label: "Banner editorial", quantity: 1, width: 1600, height: 900, ratio: "16:9" },
  { key: "support", label: "Atendimento", quantity: 1, width: 1000, height: 800, ratio: "5:4" },
];

export const standardByKey = (key: string): ImageStandard | undefined =>
  imageStandards.find((item) => item.key === key);

/* ------------------------------------------------------------------ */
/* Utilidades                                                           */
/* ------------------------------------------------------------------ */

export const parseImageDims = (url: string): { width: number; height: number } => {
  const width = Number(/[?&]width=(\d+)/.exec(url)?.[1] ?? 0);
  const height = Number(/[?&]height=(\d+)/.exec(url)?.[1] ?? 0);
  return { width, height };
};

export const estimateFileSize = (width: number, height: number): string => {
  if (!width || !height) return "—";
  const kb = Math.round((width * height * 0.07) / 1024);
  return `~${kb} KB`;
};

export const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));

export const currentRatioLabel = (width: number, height: number): string => {
  if (!width || !height) return "—";
  const divisor = gcd(width, height);
  return `${width / divisor}:${height / divisor}`;
};

/* ------------------------------------------------------------------ */
/* Inventário completo de imagens (calculado)                           */
/* ------------------------------------------------------------------ */

const SINGLE_IMAGES: Omit<ImageAsset, "standardKey" | "origin" | "status">[] = [
  {
    id: "hero-main",
    url: "https://readdy.ai/api/search-image?query=Editorial%20fashion%20photography%20of%20a%20confident%20Brazilian%20woman%20with%20natural%20curly%20hair%20wearing%20an%20elegant%20contemporary%20feminine%20outfit%20in%20deep%20plum%20and%20cream%20tones%2C%20standing%20in%20a%20warm%20sunlit%20urban%20Brazilian%20setting%20with%20soft%20neutral%20walls%2C%20natural%20daylight%2C%20realistic%20skin%20texture%2C%20warm%20welcoming%20mood&width=1600&height=1000&seq=amora-hero-main-01&orientation=landscape",
    label: "Hero principal da home",
    page: "Página inicial",
    section: "Hero principal",
    related: "—",
    purpose: "Imagem de fundo do hero",
    variant: "Desktop",
    alt: "Mulher brasileira usando look contemporâneo da nova coleção Amora Belle",
  },
  {
    id: "editorial",
    url: "https://readdy.ai/api/search-image?query=Editorial%20fashion%20photography%20of%20an%20elegant%20Brazilian%20woman%20in%20a%20refined%20feminine%20look%20with%20warm%20plum%20and%20beige%20tones%2C%20standing%20in%20a%20sunlit%20Brazilian%20urban%20interior%20with%20soft%20neutral%20walls%2C%20natural%20daylight%2C%20realistic%20skin%20texture%2C%20welcoming%20mood&width=1600&height=900&seq=amora-banner-editorial-01&orientation=landscape",
    label: "Banner editorial",
    page: "Página inicial",
    section: "Banner editorial",
    related: "—",
    purpose: "Fundo da faixa editorial",
    variant: "Desktop",
    alt: "Mulher brasileira com look elegante e natural em cenário urbano iluminado",
  },
  {
    id: "brand-story",
    url: "https://readdy.ai/api/search-image?query=Editorial%20behind%20the%20scenes%20photo%20of%20a%20clothing%20rack%20with%20feminine%20garments%20in%20warm%20plum%20cream%20and%20beige%20tones%2C%20a%20hand%20organizing%20soft%20fabrics%20in%20a%20minimal%20warm%20atelier%20with%20natural%20daylight%2C%20no%20text%2C%20no%20logos&width=900&height=1100&seq=amora-historia-bastidores-01&orientation=portrait",
    label: "História da marca",
    page: "Página inicial",
    section: "História da marca",
    related: "—",
    purpose: "Imagem da seção Nossa essência",
    variant: "Geral",
    alt: "Arara de roupas femininas organizada no ateliê da Amora Belle",
  },
  {
    id: "support",
    url: "https://readdy.ai/api/search-image?query=Warm%20lifestyle%20photo%20of%20a%20Brazilian%20fashion%20consultant%20helping%20a%20client%20choose%20clothes%20in%20a%20cozy%20boutique%20with%20neutral%20beige%20interiors%2C%20natural%20daylight%2C%20realistic%20skin%20texture%2C%20friendly%20genuine%20expressions%2C%20no%20text%2C%20no%20logos&width=1000&height=800&seq=amora-atendimento-consultora-01&orientation=landscape",
    label: "Atendimento",
    page: "Página inicial",
    section: "Atendimento",
    related: "—",
    purpose: "Imagem da seção de atendimento humanizado",
    variant: "Geral",
    alt: "Consultora de moda da Amora Belle ajudando uma cliente a escolher as peças",
  },
];

const singleStandard: Record<string, string> = {
  "hero-main": "hero-home",
  editorial: "editorial",
  "brand-story": "brand-story",
  support: "support",
};

export const buildImageInventory = (): ImageAsset[] => {
  const assets: ImageAsset[] = [];

  // Produtos e cores
  products.forEach((product) => {
    product.colors.forEach((color) => {
      assets.push({
        id: `${product.id}-${color.name}`,
        url: color.image,
        label: `${product.name} — ${color.name}`,
        page: `/produto/${product.id}`,
        section: "Produto",
        related: `${product.code} · ${product.category}`,
        purpose: `Imagem da variação de cor ${color.name}`,
        standardKey: "product",
        variant: "Geral",
        alt: `${product.name} na cor ${color.name} — Amora Belle`,
        origin: "Imagem do catálogo Amora Belle",
        status: "Publicada",
      });
    });
  });

  // Categorias: card + hero desktop + hero mobile
  categories.forEach((category) => {
    const categoryPage = category.id === "novidades" ? "/novidades" : `/categoria/${category.id}`;
    assets.push({
      id: `cat-card-${category.id}`,
      url: category.image,
      label: `${category.name} — card de categoria`,
      page: "Página inicial",
      section: "Categorias",
      related: category.name,
      purpose: "Imagem do card de categoria",
      standardKey: "category-card",
      variant: "Desktop/Mobile",
      alt: `Card da categoria ${category.name} — Amora Belle`,
      origin: "Imagem de categoria Amora Belle",
      status: "Publicada",
    });
    assets.push({
      id: `cat-hero-desk-${category.id}`,
      url: category.heroDesktop,
      label: `${category.name} — hero desktop`,
      page: categoryPage,
      section: "Hero de categoria",
      related: category.name,
      purpose: "Banner horizontal do topo da categoria",
      standardKey: "category-hero-desktop",
      variant: "Desktop",
      alt: `Banner da categoria ${category.name} — Amora Belle`,
      origin: "Imagem de categoria Amora Belle",
      status: "Publicada",
    });
    assets.push({
      id: `cat-hero-mob-${category.id}`,
      url: category.heroMobile,
      label: `${category.name} — hero mobile`,
      page: categoryPage,
      section: "Hero de categoria",
      related: category.name,
      purpose: "Banner vertical do topo da categoria no celular",
      standardKey: "category-hero-mobile",
      variant: "Mobile",
      alt: `Banner mobile da categoria ${category.name} — Amora Belle`,
      origin: "Imagem de categoria Amora Belle",
      status: "Publicada",
    });
  });

  // Looks
  lookMoments.forEach((moment) => {
    moment.images.forEach((url, index) => {
      assets.push({
        id: `look-${moment.id}-${index}`,
        url,
        label: `${moment.title} — look ${index + 1}`,
        page: "Página inicial",
        section: "Looks para cada momento",
        related: moment.title,
        purpose: "Imagem de composição de look",
        standardKey: "look",
        variant: "Geral",
        alt: `Look da seção ${moment.title} — Amora Belle`,
        origin: "Imagem editorial Amora Belle",
        status: "Publicada",
      });
    });
  });

  // Inspirações
  inspirations.forEach((item) => {
    assets.push({
      id: `insp-${item.id}`,
      url: item.image,
      label: `${item.label}`,
      page: "Página inicial",
      section: "Inspirações",
      related: item.label,
      purpose: "Imagem da galeria de inspirações",
      standardKey: item.tall ? "inspiration-tall" : "inspiration-square",
      variant: "Geral",
      alt: `${item.label} — inspirações Amora Belle`,
      origin: "Imagem editorial Amora Belle",
      status: "Publicada",
    });
  });

  // Imagens únicas
  SINGLE_IMAGES.forEach((item) => {
    assets.push({
      ...item,
      standardKey: singleStandard[item.id] ?? "editorial",
      origin: "Imagem editorial Amora Belle",
      status: "Publicada",
    });
  });

  return assets;
};

/* ------------------------------------------------------------------ */
/* Estatísticas da ficha do site                                        */
/* ------------------------------------------------------------------ */

export interface SiteStatGroup {
  title: string;
  items: { label: string; value: string; hint?: string }[];
}

export const ROUTES = [
  "/",
  "/novidades",
  "/categoria/:slug",
  "/produto/:id",
  "/sacola",
  "/checkout",
  "/painel",
];

export const buildSiteStats = (inventory: ImageAsset[]): SiteStatGroup[] => {
  const colorCount = products.reduce((sum, product) => sum + product.colors.length, 0);
  const sizeCount = new Set(products.flatMap((product) => product.sizes)).size;
  const byType = imageStandards.map((standard) => ({
    label: standard.label,
    value: String(inventory.filter((asset) => asset.standardKey === standard.key).length),
  }));

  return [
    {
      title: "Estrutura",
      items: [
        { label: "Páginas e rotas", value: String(ROUTES.length) },
        { label: "Seções na página inicial", value: "14" },
        { label: "Categorias", value: String(categories.length), hint: "7 principais + Novidades" },
        { label: "Produtos no catálogo", value: String(products.length) },
      ],
    },
    {
      title: "Variações",
      items: [
        { label: "Cores / variações", value: String(colorCount) },
        { label: "Tamanhos distintos", value: String(sizeCount) },
        { label: "Modalidades de entrega", value: String(deliveryMethods.length) },
        { label: "Integrações demonstrativas", value: "3", hint: "WhatsApp, Instagram e site Vellora" },
      ],
    },
    {
      title: "Imagens",
      items: [
        { label: "Total de imagens", value: String(inventory.length) },
        ...byType,
      ],
    },
    {
      title: "Operação",
      items: [
        { label: "Formulários", value: "1", hint: "Finalização da sacola" },
        { label: "Links de navegação", value: String(mainNav.length + 10) },
        { label: "Indexação", value: "index, follow" },
        { label: "Responsividade", value: "320px a desktop", hint: "Validada" },
      ],
    },
    {
      title: "Versão",
      items: [
        { label: "Versão atual do site", value: "Versão 4" },
        { label: "Última alteração", value: "19/09/2026" },
        { label: "Responsável pela alteração", value: "Vellora Studio" },
        { label: "Origem dos dados", value: "Site Amora Belle", hint: "Modelo demonstrativo" },
      ],
    },
  ];
};